import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';
import type {
  ReturnActionResult,
  ReturnCheckRequest,
  ReturnCheckResult,
  ReturnConfirmRequest,
  ReturnConfirmResult,
  ReturnCreateRequest,
  ReturnDetail,
  ReturnListQuery,
  ReturnPickupRequest,
  ReturnPickupResult,
  ReturnRejectRequest,
  ReturnRow
} from '../types/return';
import type { PageResult } from '../types/result';

const BASE = '/wl/return';

/**
 * 退换货（Hq.Api）。状态机：
 * NEW → SUBMITTED → AUDITED → PICKING → RECEIVED → CHECKED → REFUNDED / EXCHANGED → CLOSED，异常 REJECTED。
 */
export function createReturnApi(http: HttpClient) {
  return {
    list(query: ReturnListQuery = {}): Promise<PageResult<ReturnRow>> {
      return http.request<PageResult<ReturnRow>>({ path: BASE, query: toQuery(query) });
    },

    detail(id: Id): Promise<ReturnDetail> {
      return http.request<ReturnDetail>({ path: `${BASE}/${id}` });
    },

    /** 建单（幂等：X-Request-Id 重放返回同一张单）。 */
    create(body: ReturnCreateRequest): Promise<ReturnDetail> {
      return http.request<ReturnDetail>({ path: BASE, method: 'POST', body });
    },

    /** 生成退货取件配送单并置 PICKING。 */
    pickup(id: Id, body: ReturnPickupRequest = {}): Promise<ReturnPickupResult> {
      return http.request<ReturnPickupResult>({ path: `${BASE}/${id}/pickup`, method: 'POST', body });
    },

    /**
     * 确认回仓：按实收数量增加库存并写 RETURN_IN 流水，置 RECEIVED。
     * 少收/破损必须通过 `body.items` 录入实收，否则按应收数量全额回仓。
     */
    confirm(id: Id, body?: ReturnConfirmRequest): Promise<ReturnConfirmResult> {
      return http.request<ReturnConfirmResult>({
        path: `${BASE}/${id}/confirm`,
        method: 'POST',
        body: body ?? {}
      });
    },

    /** 责任判定：REFUND → REFUNDED；EXCHANGE → 生成补发配送单并置 EXCHANGED。 */
    check(id: Id, body: ReturnCheckRequest): Promise<ReturnCheckResult> {
      return http.request<ReturnCheckResult>({ path: `${BASE}/${id}/check`, method: 'POST', body });
    },

    /** 驳回不予受理（仅回仓前）。 */
    reject(id: Id, body: ReturnRejectRequest = {}): Promise<ReturnActionResult> {
      return http.request<ReturnActionResult>({ path: `${BASE}/${id}/reject`, method: 'POST', body });
    },

    /** 关单：REFUNDED / EXCHANGED → CLOSED。 */
    close(id: Id): Promise<ReturnActionResult> {
      return http.request<ReturnActionResult>({ path: `${BASE}/${id}/close`, method: 'POST' });
    }
  };
}

export type ReturnApi = ReturnType<typeof createReturnApi>;
